import { Link, Outlet, useLocation } from "react-router-dom";

import {
  HomeIcon,
  ChartBarIcon,
  CashIcon,
  UsersIcon,
  TruckIcon,
  LocationMarkerIcon,
  ViewGridIcon,
  CogIcon,
  UserCircleIcon,
  QuestionMarkCircleIcon,
  LogoutIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  MenuIcon,
  XIcon,
  ReceiptTaxIcon,
  CreditCardIcon,
  CollectionIcon,
  TagIcon,
  ClipboardListIcon,
  ShieldCheckIcon,
} from "@heroicons/react/outline";

import { useSelector, useDispatch } from "react-redux";

import {
  selectCurrentUser,
  logOut,
} from "../Features/auth/authSlice";

import { useLogoutMutation } from "../Features/usersSlice";

import { useEffect, useState } from "react";

import swal from "sweetalert";

import Logo from "../Views/Logo";


function ClientUserLayout() {
  const dispatch = useDispatch();
  const user = useSelector(selectCurrentUser);
  const location = useLocation();

  const [collapsed, setCollapsed] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [openSection, setOpenSection] = useState("reports");

  const [logout] = useLogoutMutation();


  // =========================================================
  // SIDEBAR COLLAPSE STATE
  // =========================================================

  useEffect(() => {
    const saved = localStorage.getItem("sidebar-collapsed");

    if (saved !== null) {
      setCollapsed(JSON.parse(saved));
    }
  }, []);


  useEffect(() => {
    localStorage.setItem(
      "sidebar-collapsed",
      JSON.stringify(collapsed)
    );
  }, [collapsed]);


  // =========================================================
  // CLOSE MOBILE NAV WHEN ROUTE CHANGES
  // =========================================================

  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);


  // =========================================================
  // PREVENT BODY SCROLL WHEN MOBILE NAV IS OPEN
  // =========================================================

  useEffect(() => {
    if (mobileNavOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileNavOpen]);


  // =========================================================
  // LOGOUT
  // =========================================================

  const logUserOut = async () => {
    try {
      await logout().unwrap();

      dispatch(logOut());
    } catch (err) {
      swal(
        "Error",
        err?.message || "Unable to logout",
        "error"
      );

      dispatch(logOut());
    }
  };


  // =========================================================
  // ACTIVE LINK
  // =========================================================

  const isActive = (path) => {
    if (path === "/client") {
      return location.pathname === "/client";
    }

    return (
      location.pathname === path ||
      location.pathname.startsWith(`${path}/`)
    );
  };


  // =========================================================
  // NAVIGATION
  // =========================================================

  const navigation = [

    // -------------------------------------------------------
    // DASHBOARD
    // -------------------------------------------------------

    {
      type: "link",
      label: "Dashboard",
      icon: HomeIcon,
      path: "/client",
    },


    // -------------------------------------------------------
    // REPORTS
    // -------------------------------------------------------

    {
      type: "section",
      id: "reports",
      label: "Reports",
      icon: ChartBarIcon,

      items: [

        {
          label: "Sales Summary",
          icon: ChartBarIcon,
          path: "/client",
        },

        {
          label: "Sales by Route",
          icon: LocationMarkerIcon,
          path: "/client/sales-by-route",
        },

        {
          label: "Sales by Category",
          icon: CollectionIcon,
          path: "/client/sales-by-category",
        },

        {
          label: "Sales by Employee",
          icon: UsersIcon,
          path: "/client/sales-by-employee",
        },

        {
          label: "Payment Types",
          icon: CreditCardIcon,
          path: "/client/sales-by-payment-type",
        },

        {
          label: "Receipts",
          icon: ReceiptTaxIcon,
          path: "/client/receipts",
        },

        {
          label: "Discounts",
          icon: TagIcon,
          path: "/client/sales-by-discount-name",
        },

        {
          label: "Taxes",
          icon: ReceiptTaxIcon,
          path: "/client/sales-by-tax-name",
        },

        {
          label: "Shifts",
          icon: ClipboardListIcon,
          path: "/client/shifts",
        },

      ],
    },


    // -------------------------------------------------------
    // OPERATIONS
    // -------------------------------------------------------

    {
      type: "section",
      id: "operations",
      label: "Operations",
      icon: TruckIcon,

      items: [

        {
          label: "Routes",
          icon: LocationMarkerIcon,
          path: "/client/routes-management",
        },

        {
          label: "Vehicles",
          icon: TruckIcon,
          path: "/client/manage-vehicles",
        },

        {
          label: "Seats",
          icon: ViewGridIcon,
          path: "/client/manage-seats",
        },

        {
          label: "Categories",
          icon: CollectionIcon,
          path: "/client/manage-categories",
        },

        {
          label: "Discounts",
          icon: TagIcon,
          path: "/client/manage-discounts",
        },

      ],
    },


    // -------------------------------------------------------
    // PEOPLE
    // -------------------------------------------------------

    {
      type: "section",
      id: "Employees",
      label: "Employees",
      icon: UsersIcon,

      items: [

        {
          label: "Employees",
          icon: UsersIcon,
          path: "/client/manage-employees",
        },

        {
          label: "Access Rights",
          icon: ShieldCheckIcon,
          path: "/client/manage-user-employee-roles",
        },

      ],
    },


    // -------------------------------------------------------
    // SETTINGS
    // -------------------------------------------------------

    {
      type: "section",
      id: "settings",
      label: "Settings",
      icon: CogIcon,

      items: [

        {
          label: "Payment Types",
          icon: CreditCardIcon,
          path: "/client/payment-types",
        },

        {
          label: "Taxes",
          icon: ReceiptTaxIcon,
          path: "/client/manage-taxes",
        },

        {
          label: "Receipt",
          icon: ReceiptTaxIcon,
          path: "/client/receipt",
        },

        {
          label: "Subscription",
          icon: CashIcon,
          path: "/user/users",
        },

      ],
    },

  ];


  // =========================================================
  // ACTIVE SECTION
  // =========================================================

  const sectionHasActiveItem = (section) => {
    return section.items?.some((item) =>
      isActive(item.path)
    );
  };


  // =========================================================
  // TOGGLE SECTION
  // =========================================================

  const toggleSection = (id) => {

    if (collapsed) {
      setCollapsed(false);
      setOpenSection(id);
      return;
    }

    setOpenSection((current) =>
      current === id ? null : id
    );
  };


  // =========================================================
  // NAVIGATION CONTENT
  // =========================================================

  const renderNavigation = () => (
    <>

      {!collapsed && (
        <div className="px-2 mb-3">
          <p className="
            text-[10px]
            font-semibold
            uppercase
            tracking-[0.15em]
            text-gray-400
            dark:text-gray-500
          ">
            Main Menu
          </p>
        </div>
      )}


      <div className="space-y-1">

        {navigation.map((item) => {

          // =================================================
          // DIRECT LINK
          // =================================================

          if (item.type === "link") {

            const Icon = item.icon;
            const active = isActive(item.path);

            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setMobileNavOpen(false)}
                className={`
                  group
                  flex
                  items-center
                  ${
                    collapsed
                      ? "justify-center"
                      : ""
                  }
                  gap-3
                  px-2
                  py-2
                  rounded-xl
                  transition
                  ${
                    active
                      ? `
                        bg-[#80b643]/10
                        dark:bg-[#80b643]/20
                        text-[#80b643]
                      `
                      : `
                        text-gray-600
                        dark:text-gray-300
                        hover:bg-gray-50
                        dark:hover:bg-gray-800
                        hover:text-gray-900
                        dark:hover:text-white
                      `
                  }
                `}
              >

                <div
                  className={`
                    flex
                    items-center
                    justify-center
                    w-9
                    h-9
                    flex-shrink-0
                    rounded-xl
                    ${
                      active
                        ? `
                          bg-[#80b643]/10
                          dark:bg-[#80b643]/20
                        `
                        : `
                          bg-gray-50
                          dark:bg-gray-800
                          group-hover:bg-gray-100
                          dark:group-hover:bg-gray-700
                        `
                    }
                  `}
                >

                  <Icon className="w-[19px] h-[19px]" />

                </div>


                {!collapsed && (
                  <span className="text-sm font-medium">
                    {item.label}
                  </span>
                )}

              </Link>
            );
          }


          // =================================================
          // SECTION
          // =================================================

          const Icon = item.icon;

          const active =
            sectionHasActiveItem(item);

          const opened =
            openSection === item.id;


          return (
            <div key={item.id}>

              <button
                type="button"
                onClick={() =>
                  toggleSection(item.id)
                }
                className={`
                  group
                  w-full
                  flex
                  items-center
                  ${
                    collapsed
                      ? "justify-center"
                      : "justify-between"
                  }
                  gap-3
                  px-2
                  py-2
                  rounded-xl
                  transition
                  ${
                    active
                      ? `
                        bg-[#80b643]/10
                        dark:bg-[#80b643]/20
                        text-[#80b643]
                      `
                      : `
                        text-gray-600
                        dark:text-gray-300
                        hover:bg-gray-50
                        dark:hover:bg-gray-800
                        hover:text-gray-900
                        dark:hover:text-white
                      `
                  }
                `}
              >

                <div className="
                  flex
                  items-center
                  gap-3
                ">

                  <div
                    className={`
                      flex
                      items-center
                      justify-center
                      w-9
                      h-9
                      flex-shrink-0
                      rounded-xl
                      ${
                        active
                          ? `
                            bg-[#80b643]/10
                            dark:bg-[#80b643]/20
                          `
                          : `
                            bg-gray-50
                            dark:bg-gray-800
                            group-hover:bg-gray-100
                            dark:group-hover:bg-gray-700
                          `
                      }
                    `}
                  >

                    <Icon className="w-[19px] h-[19px]" />

                  </div>


                  {!collapsed && (
                    <span className="
                      text-sm
                      font-medium
                    ">
                      {item.label}
                    </span>
                  )}

                </div>


                {!collapsed && (
                  opened ? (
                    <ChevronDownIcon
                      className="
                        w-4
                        h-4
                        text-gray-400
                      "
                    />
                  ) : (
                    <ChevronRightIcon
                      className="
                        w-4
                        h-4
                        text-gray-400
                      "
                    />
                  )
                )}

              </button>


              {/* =================================================
                  SUBMENU
              ================================================== */}

              {!collapsed &&
                opened && (
                  <div className="
                    relative
                    ml-[25px]
                    pl-[25px]
                    py-1
                  ">

                    <div className="
                      absolute
                      left-[8px]
                      top-1
                      bottom-1
                      w-px
                      bg-gray-200
                      dark:bg-gray-800
                    " />


                    <div className="space-y-0.5">

                      {item.items.map(
                        (subItem) => {

                          const SubIcon =
                            subItem.icon;

                          const subActive =
                            isActive(
                              subItem.path
                            );

                          return (
                            <Link
                              key={subItem.path}
                              to={subItem.path}
                              onClick={() =>
                                setMobileNavOpen(false)
                              }
                              className={`
                                group
                                relative
                                flex
                                items-center
                                gap-3
                                px-3
                                py-2
                                rounded-lg
                                text-sm
                                transition
                                ${
                                  subActive
                                    ? `
                                      bg-[#80b643]/10
                                      dark:bg-[#80b643]/20
                                      text-[#80b643]
                                      font-medium
                                    `
                                    : `
                                      text-gray-500
                                      dark:text-gray-400
                                      hover:text-gray-900
                                      dark:hover:text-white
                                      hover:bg-gray-50
                                      dark:hover:bg-gray-800
                                    `
                                }
                              `}
                            >

                              {subActive && (
                                <span className="
                                  absolute
                                  -left-[25px]
                                  top-1/2
                                  -translate-y-1/2
                                  w-1
                                  h-5
                                  rounded-r-full
                                  bg-[#80b643]
                                " />
                              )}


                              <SubIcon
                                className={`
                                  w-4
                                  h-4
                                  flex-shrink-0
                                  ${
                                    subActive
                                      ? "text-[#80b643]"
                                      : "text-gray-400 dark:text-gray-500"
                                  }
                                `}
                              />

                              <span>
                                {subItem.label}
                              </span>

                            </Link>
                          );
                        }
                      )}

                    </div>

                  </div>
                )}

            </div>
          );
        })}

      </div>
    </>
  );


  return (
    <div className="
      flex
      w-full
      h-screen
      overflow-hidden
      bg-gray-100
      dark:bg-gray-950
      text-gray-800
      dark:text-white
    ">


      {/* =====================================================
          MOBILE OVERLAY
      ====================================================== */}

      {mobileNavOpen && (
        <div
          className="
            fixed
            inset-0
            z-40
            bg-black/40
            backdrop-blur-[1px]
            lg:hidden
          "
          onClick={() =>
            setMobileNavOpen(false)
          }
        />
      )}


      {/* =====================================================
          SIDEBAR
      ====================================================== */}

      <aside
        className={`
          fixed
          lg:relative
          inset-y-0
          left-0
          z-50
          lg:z-auto

          flex
          flex-col
          flex-shrink-0
          h-screen

          bg-white
          dark:bg-gray-900

          border-r
          border-gray-200
          dark:border-gray-800

          transition-all
          duration-300
          ease-in-out

          ${
            mobileNavOpen
              ? "translate-x-0"
              : "-translate-x-full"
          }

          lg:translate-x-0

          ${
            collapsed
              ? "lg:w-[76px]"
              : "lg:w-[255px]"
          }

          w-[275px]
        `}
      >


        {/* =================================================
            HEADER
        ================================================== */}

        <div
          className={`
            h-[68px]
            flex
            items-center
            flex-shrink-0
            border-b
            border-gray-200
            dark:border-gray-800

            ${
              collapsed
                ? "lg:justify-center"
                : "justify-between px-4"
            }
          `}
        >

          <div
            className={`
              ${
                collapsed
                  ? "hidden lg:block"
                  : "block"
              }
            `}
          >
            <Logo />
          </div>


          {/* DESKTOP COLLAPSE BUTTON */}

          <button
            type="button"
            onClick={() =>
              setCollapsed((value) => !value)
            }
            className="
              hidden
              lg:flex
              items-center
              justify-center
              w-9
              h-9
              rounded-xl
              text-gray-500
              dark:text-gray-400
              hover:text-[#80b643]
              hover:bg-[#80b643]/10
              dark:hover:bg-[#80b643]/20
              transition
            "
          >
            <MenuIcon className="w-5 h-5" />
          </button>


          {/* MOBILE CLOSE BUTTON */}

          <button
            type="button"
            onClick={() =>
              setMobileNavOpen(false)
            }
            className="
              flex
              lg:hidden
              items-center
              justify-center
              w-9
              h-9
              rounded-xl
              text-gray-500
              dark:text-gray-400
              hover:text-[#80b643]
              hover:bg-[#80b643]/10
              dark:hover:bg-[#80b643]/20
              transition
            "
          >
            <XIcon className="w-5 h-5" />
          </button>

        </div>


        {/* =================================================
            NAVIGATION
        ================================================== */}

        <div className="
          flex-1
          overflow-y-auto
          px-3
          py-5
          custom-scroll
        ">

          {renderNavigation()}

        </div>


        {/* =================================================
            BOTTOM USER AREA
        ================================================== */}

        <div className="
          flex-shrink-0
          border-t
          border-gray-200
          dark:border-gray-800
          p-3
        ">

          {/* PROFILE */}

          <Link
            to="/client/user-profile"
            onClick={() =>
              setMobileNavOpen(false)
            }
            className="
              group
              flex
              items-center
              gap-3
              px-2
              py-2
              rounded-xl
              hover:bg-gray-50
              dark:hover:bg-gray-800
              transition
            "
          >

            <div className="
              flex
              items-center
              justify-center
              w-9
              h-9
              flex-shrink-0
              rounded-xl
              bg-gray-100
              dark:bg-gray-800
              text-gray-500
              dark:text-gray-400
              group-hover:text-[#80b643]
            ">
              <UserCircleIcon className="w-5 h-5" />
            </div>


            {!collapsed && (
              <div className="min-w-0">

                <p className="
                  text-sm
                  font-medium
                  truncate
                  text-gray-700
                  dark:text-gray-200
                ">
                  {user?.name || "My Profile"}
                </p>

                <p className="
                  text-[11px]
                  text-gray-400
                  dark:text-gray-500
                ">
                  Account settings
                </p>

              </div>
            )}

          </Link>


          {/* HELP / LOGOUT */}

          <div className="
            mt-1
            grid
            grid-cols-2
            gap-1
          ">

            <button
              type="button"
              className="
                flex
                items-center
                justify-center
                gap-2
                px-2
                py-2
                rounded-lg
                text-gray-500
                dark:text-gray-400
                hover:text-[#80b643]
                hover:bg-[#80b643]/10
                dark:hover:bg-[#80b643]/20
                transition
              "
              title="Help"
            >
              <QuestionMarkCircleIcon className="w-5 h-5" />

              {!collapsed && (
                <span className="text-xs">
                  Help
                </span>
              )}
            </button>


            <button
              type="button"
              onClick={logUserOut}
              className="
                flex
                items-center
                justify-center
                gap-2
                px-2
                py-2
                rounded-lg
                text-gray-500
                dark:text-gray-400
                hover:text-red-600
                dark:hover:text-red-400
                hover:bg-red-50
                dark:hover:bg-red-900/20
                transition
              "
              title="Logout"
            >
              <LogoutIcon className="w-5 h-5" />

              {!collapsed && (
                <span className="text-xs">
                  Logout
                </span>
              )}
            </button>

          </div>

        </div>

      </aside>


      {/* =====================================================
          MAIN CONTENT
      ====================================================== */}

      <main className="
        flex-1
        min-w-0
        h-screen
        overflow-y-auto
        custom-scroll
        bg-gray-100
        dark:bg-gray-950
      ">

        {/* =================================================
            MOBILE TOP BAR
        ================================================== */}

        <div className="
          lg:hidden
          sticky
          top-0
          z-30
          flex
          items-center
          justify-between
          h-[60px]
          px-4
          bg-white
          dark:bg-gray-900
          border-b
          border-gray-200
          dark:border-gray-800
        ">

          <button
            type="button"
            onClick={() =>
              setMobileNavOpen(true)
            }
            className="
              flex
              items-center
              justify-center
              w-10
              h-10
              rounded-xl
              bg-[#80b643]/10
              dark:bg-[#80b643]/20
              text-[#80b643]
              hover:bg-[#80b643]/20
              dark:hover:bg-[#80b643]/30
              transition
            "
          >
            <MenuIcon className="w-5 h-5" />
          </button>


          <div className="flex-1 flex justify-center">
            <Logo />
          </div>


          <Link
            to="/client/user-profile"
            className="
              flex
              items-center
              justify-center
              w-10
              h-10
              rounded-xl
              bg-gray-100
              dark:bg-gray-800
              text-gray-500
              dark:text-gray-400
            "
          >
            <UserCircleIcon className="w-5 h-5" />
          </Link>

        </div>


        {/* =================================================
            PAGE
        ================================================== */}

        <div className="
          min-h-full
          p-4
          sm:p-5
          lg:p-6
        ">

          <Outlet />

        </div>

      </main>

    </div>
  );
}

export default ClientUserLayout;